import { useMutation } from '@apollo/client';
import { useMemo, useState } from 'react';

import { standardError } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import { BATCH_WORK_ASSIGNMENTS_MAKE_ACTUAL_V2 } from 'gql';
import { createAssignmentErrorNotification } from 'utils/assignmentCustomErrorGenerator';

import {
	buildStationTimeSlots,
	buildInitialValues,
	buildTimeRangeInputs,
	validateChain,
} from './utils';

export const useStationEditor = ({
	sequences,
	stations,
	stationActuals,
	sequenceGroupId,
	firstStation,
	lastStation,
	onSaveSuccess,
	onCancel,
}) => {
	const stationTimeSlots = useMemo(
		() => buildStationTimeSlots(sequences, stations),
		[sequences, stations]
	);

	const initialValues = useMemo(
		() => buildInitialValues({ stationTimeSlots, stations, stationActuals }),
		[stationTimeSlots, stations, stationActuals]
	);

	const [values, setValues] = useState(initialValues);
	const [timeOrderError, setChainError] = useState(null);

	const isDirty = useMemo(
		() => Object.keys(initialValues).some((key) => values[key] !== initialValues[key]),
		[values, initialValues]
	);

	const fieldsByStation = useMemo(
		() =>
			stationTimeSlots.reduce((acc, desc) => {
				const { stationIndex, position } = desc;

				if (!acc[stationIndex]) acc[stationIndex] = {};

				acc[stationIndex][position] = `${stationIndex}:${position}`;

				return acc;
			}, {}),
		[stationTimeSlots]
	);

	const handleChange = (fieldKey, newTime) => {
		const next = { ...values, [fieldKey]: newTime };
		setValues(next);
		setChainError(validateChain({ values: next, stations, stationTimeSlots }));
	};

	const handleCancel = () => {
		setValues(initialValues);
		setChainError(null);
		onCancel();
	};

	const [batchUpdate, { loading: isSaving }] = useMutation(BATCH_WORK_ASSIGNMENTS_MAKE_ACTUAL_V2, {
		notifyOnNetworkStatusChange: true,
		onCompleted: (data) => {
			const payload = data?.batchWorkAssignmentsMakeActualV2;

			if (payload?.__typename === 'WorkAssignmentBatchMutationSuccessResponseV2') {
				const sequence = payload?.workAssignmentSequences?.[0];

				notificationStore.setNotification({
					header: 'Success!',
					label: `Work assignment for ${sequence?.crewMember?.fullName ?? 'crew member'} for segment ${
						sequence?.fromStation?.code ?? firstStation?.code ?? 'N/A'
					} → ${sequence?.toStation?.code ?? lastStation?.code ?? 'N/A'} was manually reconciled.`,
					appearance: 'success',
				});
				onSaveSuccess();
				return;
			}

			const errorList = payload?.errors?.flatMap((e) => e?.errors ?? []).filter(Boolean) ?? [];

			notificationStore.setNotification(
				createAssignmentErrorNotification(
					firstStation?.code ?? 'N/A',
					lastStation?.code ?? 'N/A',
					errorList
				)
			);
		},
		onError: () => notificationStore.setNotification(standardError),
	});

	const handleSave = () => {
		const error = validateChain({ values, stations, stationTimeSlots });

		if (error) {
			setChainError(error);
			return;
		}

		const timeRangeInputs = buildTimeRangeInputs({ stationTimeSlots, stations, values });

		if (timeRangeInputs.length === 0) {
			notificationStore.setNotification(standardError);
			return;
		}

		batchUpdate({
			variables: {
				input: {
					sequenceGroupId,
					fromStationId: firstStation?.id,
					toStationId: lastStation?.id,
					activityType: 'driving',
					timeRangeInputs,
				},
			},
		});
	};

	return {
		values,
		timeOrderError,
		isDirty,
		isSaving,
		fieldsByStation,
		handleChange,
		handleCancel,
		handleSave,
	};
};
